import React, { useState, useEffect, useCallback } from 'react';
import { api } from '../context/AuthContext';
import { FileText, Download, FolderOpen } from 'lucide-react';
import { Button } from '../components/ui/button';

export default function ResourcesPage() {
  const [resources, setResources] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState('all');

  const fetchResources = useCallback(async () => {
    try {
      const response = await api.get('/api/resources');
      setResources(response.data);
    } catch (error) {
      // Resources fetch failed silently
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchResources();
  }, [fetchResources]);

  const categories = ['all', ...new Set(resources.map(r => r.category))];
  const filteredResources = selectedCategory === 'all' 
    ? resources 
    : resources.filter(r => r.category === selectedCategory);

  const renderResourceContent = () => {
    if (loading) return <ResourcesLoading />;
    if (filteredResources.length === 0) return <ResourcesEmpty />;
    return (
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
        {filteredResources.map(resource => (
          <div
            key={resource.id}
            className="bg-[#141417] border border-[#27272A] rounded-md p-6 hover:border-[#007AFF]/50 transition-colors"
            data-testid={`resource-${resource.id}`}
          >
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 bg-[#007AFF]/10 rounded-md flex items-center justify-center flex-shrink-0">
                <FileText className="w-6 h-6 text-[#007AFF]" />
              </div>
              <div className="flex-1 min-w-0">
                <h3 className="text-white font-medium mb-1 truncate">{resource.title}</h3>
                <p className="text-[#71717A] text-sm mb-2 line-clamp-2">{resource.description}</p>
                <span className="inline-block bg-[#27272A] text-[#A1A1AA] text-xs px-2 py-1 rounded-sm">
                  {resource.category}
                </span>
              </div>
            </div>
            <div className="mt-4 pt-4 border-t border-[#27272A]">
              <Button 
                variant="ghost" 
                className="w-full text-[#007AFF] hover:bg-[#007AFF]/10"
                data-testid={`download-${resource.id}`}
              >
                <Download className="w-4 h-4 mr-2" /> Download PDF
              </Button>
            </div>
          </div>
        ))}
      </div>
    );
  };

  return (
    <div className="p-8" data-testid="resources-page">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-white mb-2" style={{ fontFamily: 'Chivo, sans-serif' }}>
          Resource Library
        </h1>
        <p className="text-[#71717A]">Access trading guides, PDFs, and educational materials</p>
      </div>

      {/* Category Filter */}
      {categories.length > 1 && (
        <div className="flex gap-2 mb-6 overflow-x-auto pb-2">
          {categories.map(cat => (
            <Button
              key={cat}
              variant={selectedCategory === cat ? 'default' : 'outline'}
              className={selectedCategory === cat ? 'btn-primary' : 'border-[#27272A] text-[#A1A1AA]'}
              onClick={() => setSelectedCategory(cat)}
              data-testid={`filter-${cat}`}
            >
              {cat.charAt(0).toUpperCase() + cat.slice(1)}
            </Button>
          ))}
        </div>
      )}

      {renderResourceContent()}
    </div>
  );
}

function ResourcesLoading() {
  return (
    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
      {[1, 2, 3, 4, 5, 6].map(i => (
        <div key={i} className="skeleton h-40 rounded-md"></div>
      ))}
    </div>
  );
}

function ResourcesEmpty() {
  return (
    <div className="bg-[#141417] border border-[#27272A] rounded-md p-12 text-center">
      <FolderOpen className="w-16 h-16 text-[#71717A] mx-auto mb-4" />
      <h2 className="text-xl font-bold text-white mb-2">No Resources Available</h2>
      <p className="text-[#71717A]">Check back later for trading guides and materials</p>
    </div>
  );
}
